import type { DsdEditorMappingReviewJudgement } from "@/apis/dsd-editor/route";
import { ModalWrapper } from "@/components/modals/modal-wrapper";
import { cn } from "@/lib/utils";
import { popModal } from "@/stores/modal";
import { Button } from "@/toggle-ui-kit/button";
import { NOTE_REFERENCE_SOFT_CLASS } from "@/utils/dsd-editor/note-reference-colors";
import type { DsdEditorMappingReviewInput } from "../use-mapping-review";
import { TriangleAlert } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { DSD_REVIEW_COPY } from "../copy";
import { TONE_RING, tagToneOf } from "./review-card-parts";
import {
  DsdReviewMatchIndicator,
  DsdReviewTagChip,
} from "../review-indicators";
import type { DsdReviewPendingItem } from "./selected-cell-view";

/** 판단 둘 — 추가 확인 필요 / 문제 없는 항목 */
const CHOICES = ["needs_follow_up", "no_issue"] as const;
type Choice = (typeof CHOICES)[number];

/**
 * 검토 모달 (Figma "Floating Panel / popover"). 화면을 어둡게 덮고 가운데 뜬다 — 모달 스택(pushModal).
 * 사유는 필수 — 비어 있으면 완료 버튼이 비활성이고 Enter 도 무시한다. 저장 실패는 훅이 토스트로 알리고 모달은 열려 있다
 */
export const DsdReviewMappingReviewModal = ({
  item,
  bannerText,
  onSave,
}: {
  /** 검토 쌍이 있는 카드만 — 없는 카드로는 열 수 없다 (타입) */
  item: DsdReviewPendingItem;
  /** 카드 안 배너 문구 — 패널 배너와 같은 판정 */
  bannerText: string | null;
  onSave: (input: DsdEditorMappingReviewInput) => Promise<unknown>;
}) => {
  const copy = DSD_REVIEW_COPY.reviewModal;
  // 수정(저장된 판단이 있음)은 완료 버튼 문구만 다르다 — 제목·부제는 처음 검토와 같다
  const editing = item.review !== null;
  const panelCopy = DSD_REVIEW_COPY.panel;
  // 기본 선택 = 추가 확인 필요. 저장된 판단이 있으면(수정) 그 판단·사유로 시작
  const [choice, setChoice] = useState<Choice>(
    item.review?.judgement ?? "needs_follow_up",
  );
  const [reason, setReason] = useState(item.review?.reason ?? "");
  const [busy, setBusy] = useState(false);
  // 모달 스택은 포커스를 옮겨 주지 않는다 — 열리자마자 ←/→·Enter 가 먹도록 본체에 포커스
  const rootRef = useRef<HTMLDivElement>(null);
  useEffect(() => rootRef.current?.focus(), []);
  // 수정인데 판단·사유가 저장된 값 그대로면 보낼 것이 없다 — 버튼 비활성, Enter 무시 (POST·캐시 패치 낭비 방지).
  // 사유만 바뀐 것은 저장 대상이다 (백엔드가 판단과 사유를 함께 비교해 다르면 새 기록을 쌓는다)
  const unchanged =
    item.review !== null &&
    choice === item.review.judgement &&
    reason.trim() === item.review.reason;
  const canSubmit = !busy && !unchanged && reason.trim() !== "";
  const submit = async () => {
    setBusy(true);
    try {
      await onSave({
        target: item.target,
        judgement: choice satisfies DsdEditorMappingReviewJudgement,
        reason: reason.trim(),
      });
      popModal();
    } catch {
      setBusy(false);
    }
  };

  // 키보드: ←/→ 판단 전환(사유 입력 중엔 커서 이동이 우선), Enter 완료(사유 입력 중엔 Cmd/Ctrl+Enter — Enter 는 줄바꿈).
  // 라디오 위의 Enter 는 preventDefault 로 클릭(전환)을 막아 방금 바뀐 값이 저장되지 않게 한다
  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const inTextarea = event.target instanceof HTMLTextAreaElement;
    if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
      if (inTextarea) return;
      event.preventDefault();
      setChoice((current) =>
        current === CHOICES[0] ? CHOICES[1] : CHOICES[0],
      );
    } else if (event.key === "Enter") {
      if (event.repeat || !canSubmit) return;
      if (inTextarea && !(event.metaKey || event.ctrlKey)) return;
      event.preventDefault();
      void submit();
    }
  };

  return (
    <ModalWrapper
      ref={rootRef}
      tabIndex={-1}
      onKeyDown={onKeyDown}
      className="flex w-[458px] min-w-[320px] max-w-[1200px] flex-col gap-4 rounded-[12px] bg-background-base p-6 outline-none"
    >
      <div>
        <h2 className="headline-2-semibold text-label-normal">{copy.title}</h2>
        <p className="mt-1 label-2-regular text-label-alternative">
          {copy.subtitle}
        </p>
      </div>

      <div
        className={cn(
          "flex flex-col gap-2 rounded-[12px] bg-background-base p-4 ring-1 ring-inset",
          TONE_RING[item.tone],
        )}
      >
        <div className="flex items-center gap-2">
          <DsdReviewTagChip
            label={panelCopy.tag[item.tag]}
            tone={tagToneOf(item)}
          />
          <p className="label-1-normal-semibold truncate text-label-normal">
            {item.title}
          </p>
          {item.axisLabel && (
            <p className="label-2-regular truncate text-label-alternative">
              {item.axisLabel}
            </p>
          )}
        </div>
        <div className="flex items-center justify-between gap-2">
          <p className="headline-2-semibold text-label-normal">
            {item.value || panelCopy.emptyValue}
          </p>
          <DsdReviewMatchIndicator
            label={item.matchLabel}
            positive={item.matchTone === "positive"}
          />
        </div>
        {bannerText && (
          <div
            className={cn(
              "flex items-center gap-2 rounded-[8px] px-3 py-2 label-2-medium",
              NOTE_REFERENCE_SOFT_CLASS[item.tone],
            )}
          >
            <TriangleAlert className="size-4 shrink-0" />
            <span>{bannerText}</span>
          </div>
        )}
      </div>

      {item.review?.inheritedFromFilename && (
        <p className="label-2-regular break-all text-label-alternative">
          {copy.inheritedFrom(item.review.inheritedFromFilename)}
        </p>
      )}

      {/* 2택: 흰 카드가 선택 */}
      <div
        role="radiogroup"
        aria-label={copy.title}
        className="flex gap-1 rounded-[8px] bg-background-neutral-alternative p-1"
      >
        {CHOICES.map((value) => (
          <button
            key={value}
            type="button"
            role="radio"
            aria-checked={choice === value}
            onClick={() => setChoice(value)}
            className={cn(
              "h-10 flex-1 rounded-[6px] label-1-normal-medium transition-colors",
              choice === value
                ? "bg-background-base text-label-normal shadow-base"
                : "text-label-alternative hover:text-label-normal",
            )}
          >
            {copy.choices[value]}
          </button>
        ))}
      </div>

      <label className="flex flex-col gap-2">
        <span className="label-2-medium text-label-normal">
          {copy.reasonLabel}
        </span>
        <textarea
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          maxLength={2000}
          required
          rows={5}
          placeholder={copy.reasonPlaceholder[item.tone][choice]}
          className="resize-none rounded-[8px] border border-line-normal px-4 py-3 body-2-normal-regular text-label-normal outline-none placeholder:text-label-assistive focus:border-primary-color"
        />
      </label>

      <div className="flex justify-end gap-2">
        <Button
          variant="outlined"
          color="secondary"
          size={40}
          disabled={busy}
          onClick={popModal}
        >
          {copy.cancel}
        </Button>
        <Button
          variant="solid"
          color="blue"
          size={40}
          disabled={!canSubmit}
          onClick={() => void submit()}
        >
          {editing ? copy.editSubmit : copy.submit}
        </Button>
      </div>
    </ModalWrapper>
  );
};
